import {
  type PlayerLike,
  Project,
  ProjectMetadata,
  type RemotionSceneDescription,
  SettingsMetadata,
} from '@cascade/core';
import {Signal, useSignal} from '@preact/signals-react';
import {createContext, ReactNode} from 'react';
import {useContext, useRef} from 'react';
import {useSubscribable} from '../hooks';
import {EditorPlugin} from '../plugin';
import {LoggerManager} from '../utils';

export interface Inspection {
  key: string;
  payload: unknown;
}

interface Application {
  project: Project;
  player: PlayerLike;
  /** Renderer stub -- typed as `any` to accept both MC Renderer and RemotionRendererStub */
  renderer: any;
  /** Presenter stub -- typed as `any` to accept both MC Presenter and PresenterStub */
  presenter: any;
  meta: ProjectMetadata;
  settings: SettingsMetadata;
  plugins: EditorPlugin[];
  logger: LoggerManager;
  inspection: Signal<Inspection>;
  remotionScenes?: RemotionSceneDescription[];
}

const ApplicationContext = createContext<Application | null>(null);

export function useApplication(): Application {
  return useContext(ApplicationContext);
}

export function ApplicationProvider({
  application,
  children,
}: {
  application: Omit<Application, 'logger' | 'inspection'>;
  children: ReactNode;
}) {
  const inspection = useSignal<Inspection>({key: '', payload: null});
  const manager = useRef<LoggerManager | null>(null);
  manager.current ??= new LoggerManager(application.project.logger);
  useSubscribable(
    application.player.onRecalculated,
    () => manager.current.clear(),
    [],
  );

  return (
    <ApplicationContext.Provider
      value={{
        ...application,
        logger: manager.current,
        inspection,
      }}
    >
      {application.plugins.reduce((children, plugin) => {
        const Component = plugin.provider;
        return Component ? (
          <Component key={plugin.name}>{children}</Component>
        ) : (
          children
        );
      }, children)}
    </ApplicationContext.Provider>
  );
}
